/**
 * Asks before "Invite Client" makes a second record for someone the advisor already has.
 *
 * Shown when the name or email typed into the invitation matches an existing client. The advisor
 * chooses: send the intake to that client, create a new one anyway, or go back. Every decision is
 * in the invite-client store; this renders the question.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import { fullName, isPlaceholderEmail } from '@shared';

import { useInviteClientStore } from '@/features/invite-client';

function formatAdded(value: Date | string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function ExistingClientModal() {
  const {
    existingClientMatch,
    dismissExistingClientMatch,
    sendToExistingClient,
    createNewClientAnyway,
    isLoading,
  } = useInviteClientStore();

  if (!existingClientMatch) return null;

  const name = fullName(existingClientMatch.clientFirstName, existingClientMatch.clientLastName);
  const added = formatAdded(existingClientMatch.clientAddedDateTime);
  // An internal stand-in address is not something the advisor ever typed or should see.
  const shownEmail = isPlaceholderEmail(existingClientMatch.clientEmail)
    ? null
    : existingClientMatch.clientEmail;
  // The server will not send a new intake to someone who has finished theirs, so it is not offered.
  const hasCompletedIntake =
    existingClientMatch.formReferenceStatus === 'completed' ||
    existingClientMatch.formReferenceEverCompleted === true;

  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open) dismissExistingClientMatch();
      }}
    >
      <DialogContent
        className="z-60 sm:max-w-lg"
        data-testid="existing-client-modal"
      >
        <DialogHeader>
          <DialogTitle>This client may already exist</DialogTitle>
          <DialogDescription>
            You already have a client named{' '}
            <span className="font-medium text-gray-900">{name}</span>
            {added ? `, added on ${added}` : ''}
            {shownEmail ? ` (${shownEmail})` : ''}.{' '}
            {hasCompletedIntake
              ? 'They have already completed their intake form. Sending this invitation would create a second record for them.'
              : 'Sending this invitation as it is would create a second record for them.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          {!hasCompletedIntake && (
            <Button
              className="w-full"
              onClick={sendToExistingClient}
              disabled={isLoading}
            >
              Send the intake to the existing client
            </Button>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={createNewClientAnyway}
            disabled={isLoading}
          >
            Create a new client anyway
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={dismissExistingClientMatch}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
